import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Save, Loader2, CalendarDays, Clock, LogOut, ShieldCheck, Check, X } from "lucide-react";
import { toast } from "sonner";
import { api, adminAxios, setToken } from "@/lib/api";

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const WEEKDAY_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function Admin() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [packages, setPackages] = useState([]);
  const [extras, setExtras] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [slotMap, setSlotMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState("");
  const [scheduleSaving, setScheduleSaving] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [meR, p, e, b, sch, slots] = await Promise.all([
        adminAxios.get(`${api}/auth/me`),
        axios.get(`${api}/packages`),
        axios.get(`${api}/extras`),
        adminAxios.get(`${api}/bookings`),
        axios.get(`${api}/schedule`),
        axios.get(`${api}/availability/slots?days=30`),
      ]);
      setMe(meR.data);
      setPackages(p.data);
      setExtras(e.data);
      setBookings(b.data);
      setSchedule(sch.data);
      const m = {};
      (slots.data.days || []).forEach(d => { m[d.date] = d; });
      setSlotMap(m);
    } catch {
      toast.error("Failed to load admin data.");
    }
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, []);

  const updatePkg = async (slug, payload) => {
    setSavingId(`pkg-${slug}`);
    try {
      const r = await adminAxios.put(`${api}/packages/${slug}`, payload);
      setPackages(packages.map(p => p.slug === slug ? r.data : p));
      toast.success(`${r.data.name} updated.`);
    } catch { toast.error("Save failed."); }
    setSavingId("");
  };

  const updateExtra = async (id, payload) => {
    setSavingId(`extra-${id}`);
    try {
      const r = await adminAxios.put(`${api}/extras/${id}`, payload);
      setExtras(extras.map(e => e.id === id ? r.data : e));
      toast.success(`${r.data.name} updated.`);
    } catch { toast.error("Save failed."); }
    setSavingId("");
  };

  const updateScheduleRule = async (weekday, patch) => {
    setScheduleSaving(true);
    try {
      const r = await adminAxios.put(`${api}/schedule/${weekday}`, patch);
      setSchedule(schedule.map(s => s.weekday === weekday ? r.data : s));
      toast.success(`${WEEKDAYS[weekday]} updated.`);
    } catch { toast.error("Could not update schedule."); }
    setScheduleSaving(false);
  };

  const confirmBooking = async (id) => {
    setSavingId(`bk-${id}`);
    try {
      const r = await adminAxios.post(`${api}/bookings/${id}/confirm`);
      setBookings(bookings.map(b => b.id === id ? r.data : b));
      toast.success("Booking confirmed. Customer notified.");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not confirm.";
      toast.error(detail);
    }
    setSavingId("");
  };

  const cancelBooking = async (id) => {
    const reason = window.prompt("Optional reason for cancellation (customer will see this):", "") ?? "";
    setSavingId(`bk-${id}`);
    try {
      const r = await adminAxios.post(`${api}/bookings/${id}/cancel`, { reason });
      setBookings(bookings.map(b => b.id === id ? r.data : b));
      // refresh availability so the slot re-opens
      const slots = await axios.get(`${api}/availability/slots?days=30`);
      const m = {};
      (slots.data.days || []).forEach(d => { m[d.date] = d; });
      setSlotMap(m);
      toast.success("Booking cancelled. Customer notified.");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not cancel.";
      toast.error(detail);
    }
    setSavingId("");
  };

  const logout = () => {
    setToken(null);
    toast.success("Logged out.");
    navigate("/admin/login", { replace: true });
  };

  // Compute upcoming days for the booking overview
  const upcomingDays = Object.values(slotMap).slice(0, 14);

  return (
    <div className="min-h-screen bg-ink text-white py-12 md:py-16 px-6 md:px-12">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-12 gap-4 flex-wrap">
          <Link data-testid="admin-back-link" to="/" className="flex items-center gap-2 text-zinc-400 hover:text-white font-mono text-xs uppercase tracking-[0.25em]">
            <ArrowLeft className="w-4 h-4" /> Back to site
          </Link>
          <div className="flex items-center gap-4">
            {me && (
              <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">
                <ShieldCheck className="w-3.5 h-3.5" /> {me.name}
              </div>
            )}
            <button data-testid="admin-logout" onClick={logout}
                    className="flex items-center gap-2 border border-white/10 hover:border-white px-3.5 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-300 hover:text-white transition-colors">
              <LogOut className="w-3.5 h-3.5" /> Log out
            </button>
          </div>
        </div>

        <div className="mb-12">
          <div className="font-mono text-[11px] uppercase tracking-[0.4em] text-zinc-500 mb-3">— Admin / GI Detailing</div>
          <h1 className="font-serif text-5xl md:text-6xl">Control room<span className="text-zinc-600">.</span></h1>
          <p className="mt-4 text-zinc-400 max-w-xl font-light">Manage prices, weekly schedule, capacity and incoming bookings. Changes apply instantly to the customer-facing site.</p>
        </div>

        {loading ? (
          <div className="flex items-center gap-3 text-zinc-400"><Loader2 className="w-4 h-4 animate-spin" /> Loading...</div>
        ) : (
          <>
            {/* Schedule */}
            <section className="mb-20">
              <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
                <div>
                  <h2 className="font-serif text-3xl flex items-center gap-3">
                    <Clock className="w-7 h-7 text-zinc-500" />
                    Weekly Schedule
                  </h2>
                  <p className="mt-2 text-sm text-zinc-400 max-w-xl font-light">Open hours, slot length and capacity for each weekday. Customers can only book during open hours and until daily capacity is reached.</p>
                </div>
              </div>
              <div className="border border-white/10">
                <div className="grid grid-cols-12 gap-3 px-5 py-3 border-b border-white/10 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                  <div className="col-span-3">Day</div>
                  <div className="col-span-2">Open</div>
                  <div className="col-span-2">Start (h)</div>
                  <div className="col-span-2">End (h)</div>
                  <div className="col-span-2">Capacity</div>
                  <div className="col-span-1 text-right">Slots</div>
                </div>
                {schedule.map(rule => (
                  <ScheduleRow key={rule.weekday} rule={rule} onSave={updateScheduleRule} saving={scheduleSaving} />
                ))}
              </div>
              <p className="mt-3 text-[11px] text-zinc-500 font-mono uppercase tracking-wider">Tip · End hour is exclusive. e.g. End 19 + slot 60 min ⇒ last slot starts at 6:00 PM.</p>
            </section>

            {/* Upcoming days overview */}
            <section className="mb-20">
              <h2 className="font-serif text-3xl mb-2 flex items-center gap-3">
                <CalendarDays className="w-7 h-7 text-zinc-500" />
                Next 14 days
              </h2>
              <p className="mb-6 text-sm text-zinc-400 font-light">Live view of capacity. Click a day to see the booked slots.</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-3" data-testid="upcoming-grid">
                {upcomingDays.map(d => {
                  const taken = (d.slots || []).reduce((acc, s) => acc + s.taken, 0);
                  const total = (d.slots || []).length * (d.capacity || 1);
                  const pct = total > 0 ? Math.round((taken / total) * 100) : 0;
                  return (
                    <div key={d.date} data-testid={`day-card-${d.date}`}
                         className={`border p-3 ${d.enabled ? (d.has_capacity ? "border-white/15 bg-surface" : "border-red-500/30 bg-red-500/5") : "border-white/5 bg-ink opacity-50"}`}>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{WEEKDAY_SHORT[d.weekday]}</div>
                      <div className="font-serif text-2xl mt-1">{new Date(d.date + "T12:00:00").getDate()}</div>
                      <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 mt-2">
                        {d.enabled ? (d.has_capacity ? `${taken}/${total} booked` : "Full") : "Closed"}
                      </div>
                      {d.enabled && total > 0 && (
                        <div className="mt-2 h-1 bg-white/10 overflow-hidden">
                          <div className={`h-full ${pct >= 100 ? "bg-red-500" : "bg-white"}`} style={{ width: `${pct}%` }} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Packages */}
            <section className="mb-20">
              <h2 className="font-serif text-3xl mb-6">Packages</h2>
              <div className="space-y-6">
                {packages.map(p => (
                  <PackageEditor key={p.slug} pkg={p} saving={savingId === `pkg-${p.slug}`} onSave={(payload) => updatePkg(p.slug, payload)} />
                ))}
              </div>
            </section>

            {/* Extras */}
            <section className="mb-20">
              <h2 className="font-serif text-3xl mb-6">Extras</h2>
              <div className="border border-white/10">
                <div className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-white/10 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                  <div className="col-span-5">Name</div>
                  <div className="col-span-3 text-right">Min ($)</div>
                  <div className="col-span-3 text-right">Max ($)</div>
                  <div className="col-span-1"></div>
                </div>
                {extras.map(e => (
                  <ExtraEditor key={e.id} extra={e} saving={savingId === `extra-${e.id}`} onSave={(payload) => updateExtra(e.id, payload)} />
                ))}
              </div>
            </section>

            {/* Bookings */}
            <section>
              <h2 className="font-serif text-3xl mb-6">Bookings <span className="font-mono text-sm text-zinc-500">({bookings.length})</span></h2>
              {bookings.length === 0 ? (
                <div className="border border-dashed border-white/10 p-8 text-center text-zinc-500 font-light text-sm">No bookings yet.</div>
              ) : (
                <div className="border border-white/10 divide-y divide-white/10" data-testid="admin-bookings-list">
                  {bookings.map(b => {
                    const isCancelled = b.status === "cancelled";
                    const isConfirmed = b.status === "confirmed";
                    const busy = savingId === `bk-${b.id}`;
                    return (
                    <div key={b.id} className={`grid md:grid-cols-12 gap-4 p-5 hover:bg-white/[0.02] ${isCancelled ? "opacity-60" : ""}`} data-testid={`booking-${b.id}`}>
                      <div className="md:col-span-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-serif text-lg">{b.full_name}</span>
                          <span data-testid={`booking-${b.id}-status`}
                                className={`font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border ${
                                  isConfirmed ? "border-emerald-500/40 text-emerald-300" :
                                  isCancelled ? "border-red-500/40 text-red-300" :
                                  "border-yellow-500/40 text-yellow-300"
                                }`}>{b.status}</span>
                        </div>
                        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mt-1">Received {new Date(b.created_at).toLocaleDateString()}</div>
                      </div>
                      <div className="md:col-span-2 text-sm">
                        <div className="text-zinc-400">{b.phone}</div>
                        <div className="text-zinc-600 text-xs">{b.email || "—"}</div>
                      </div>
                      <div className="md:col-span-2 text-sm">
                        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Vehicle</div>
                        <div>{b.vehicle}</div>
                      </div>
                      <div className="md:col-span-2 text-sm">
                        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Package</div>
                        <div>{b.package_slug}</div>
                      </div>
                      <div className="md:col-span-2 text-sm">
                        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Appointment</div>
                        <div>{b.preferred_date || "—"} <span className="text-zinc-500">·</span> <span className="font-mono">{b.preferred_time || "—"}</span></div>
                      </div>
                      <div className="md:col-span-1 flex md:justify-end items-center gap-2">
                        {!isCancelled && !isConfirmed && (
                          <button
                            data-testid={`booking-${b.id}-confirm`}
                            onClick={() => confirmBooking(b.id)}
                            disabled={busy}
                            title="Confirm booking (email + SMS to customer)"
                            className="w-8 h-8 grid place-items-center border border-emerald-500/40 hover:bg-emerald-500/10 text-emerald-300 transition-colors disabled:opacity-40"
                          >
                            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-4 h-4" />}
                          </button>
                        )}
                        {!isCancelled && (
                          <button
                            data-testid={`booking-${b.id}-cancel`}
                            onClick={() => cancelBooking(b.id)}
                            disabled={busy}
                            title="Cancel booking (notify customer)"
                            className="w-8 h-8 grid place-items-center border border-red-500/40 hover:bg-red-500/10 text-red-300 transition-colors disabled:opacity-40"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      {b.notes && <div className="md:col-span-12 text-xs text-zinc-400 pt-2 border-t border-white/5 mt-1 italic">&ldquo;{b.notes}&rdquo;</div>}
                    </div>
                  );})}
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}

function ScheduleRow({ rule, onSave, saving }) {
  const [draft, setDraft] = useState(rule);
  useEffect(() => setDraft(rule), [rule]);
  const dirty = JSON.stringify(draft) !== JSON.stringify(rule);
  const totalSlots = draft.enabled ? Math.max(0, draft.end_hour - draft.start_hour) : 0;

  return (
    <div className="grid grid-cols-12 gap-3 px-5 py-3 border-b border-white/10 last:border-b-0 items-center"
         data-testid={`schedule-row-${rule.weekday}`}>
      <div className="col-span-3 font-serif text-lg">{WEEKDAYS[rule.weekday]}</div>
      <div className="col-span-2">
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            data-testid={`schedule-${rule.weekday}-enabled`}
            checked={draft.enabled}
            onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })}
            className="w-4 h-4 accent-white"
          />
          <span className="text-sm text-zinc-300">{draft.enabled ? "Open" : "Closed"}</span>
        </label>
      </div>
      <input
        type="number" min="0" max="23"
        data-testid={`schedule-${rule.weekday}-start`}
        className="col-span-2 admin-input"
        value={draft.start_hour}
        onChange={(e) => setDraft({ ...draft, start_hour: parseInt(e.target.value || 0, 10) })}
        disabled={!draft.enabled}
      />
      <input
        type="number" min="1" max="24"
        data-testid={`schedule-${rule.weekday}-end`}
        className="col-span-2 admin-input"
        value={draft.end_hour}
        onChange={(e) => setDraft({ ...draft, end_hour: parseInt(e.target.value || 0, 10) })}
        disabled={!draft.enabled}
      />
      <input
        type="number" min="0"
        data-testid={`schedule-${rule.weekday}-capacity`}
        className="col-span-2 admin-input"
        value={draft.capacity}
        onChange={(e) => setDraft({ ...draft, capacity: parseInt(e.target.value || 0, 10) })}
        disabled={!draft.enabled}
      />
      <div className="col-span-1 flex items-center justify-end gap-2">
        <span className="font-mono text-xs text-zinc-500" data-testid={`schedule-${rule.weekday}-slots`}>{totalSlots * draft.capacity}</span>
        <button
          data-testid={`schedule-${rule.weekday}-save`}
          disabled={!dirty || saving}
          onClick={() => onSave(rule.weekday, draft)}
          title="Save"
          className="disabled:opacity-30"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className={`w-4 h-4 ${dirty ? "text-white" : "text-zinc-500"}`} />}
        </button>
      </div>
      <style>{`
        .admin-input { background:#050505; border:1px solid rgba(255,255,255,0.1); color:#fff; padding: 8px 10px; font-family:'JetBrains Mono', monospace; font-size: 13px; outline:none; text-align: center; }
        .admin-input:focus { border-color: rgba(255,255,255,0.5); }
        .admin-input:disabled { opacity: 0.4; }
      `}</style>
    </div>
  );
}

function PackageEditor({ pkg, saving, onSave }) {
  const [draft, setDraft] = useState(pkg);
  useEffect(() => setDraft(pkg), [pkg]);
  const dirty = JSON.stringify(draft) !== JSON.stringify(pkg);

  return (
    <div className="border border-white/10 bg-surface p-6" data-testid={`admin-pkg-${pkg.slug}`}>
      <div className="grid md:grid-cols-12 gap-4 mb-5">
        <div className="md:col-span-5">
          <label className="block font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Name</label>
          <input data-testid={`pkg-${pkg.slug}-name`} className="ed-input" value={draft.name} onChange={(ev)=>setDraft({...draft, name: ev.target.value})} />
        </div>
        <div className="md:col-span-7">
          <label className="block font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Duration</label>
          <input className="ed-input" value={draft.duration} onChange={(ev)=>setDraft({...draft, duration: ev.target.value})} />
        </div>
        <div className="md:col-span-12">
          <label className="block font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Description</label>
          <input className="ed-input" value={draft.description} onChange={(ev)=>setDraft({...draft, description: ev.target.value})} />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4 mb-5">
        {["sedan_price","suv_price","truck_price"].map(k => (
          <div key={k}>
            <label className="block font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">{k.replace("_price","")}</label>
            <input data-testid={`pkg-${pkg.slug}-${k}`} type="number" className="ed-input"
                   value={draft[k]} onChange={(ev)=>setDraft({...draft, [k]: parseFloat(ev.target.value || 0)})} />
          </div>
        ))}
      </div>
      <div className="mb-5">
        <label className="block font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Features (one per line)</label>
        <textarea rows={Math.max(3, draft.features.length)} className="ed-input resize-none"
                  value={draft.features.join("\n")}
                  onChange={(ev)=>setDraft({...draft, features: ev.target.value.split("\n").filter(x=>x.trim().length>0)})} />
      </div>
      <button
        data-testid={`pkg-${pkg.slug}-save`}
        disabled={!dirty || saving} onClick={() => onSave(draft)}
        className="beam-btn bg-white text-ink font-mono text-[10px] uppercase tracking-[0.25em] px-5 py-2.5 hover:bg-zinc-200 transition-colors flex items-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed"
      >
        {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save Changes
      </button>
      <style>{`
        .ed-input { width:100%; background:#050505; border:1px solid rgba(255,255,255,0.1); color:#fff; padding: 10px 12px; font-family:'Manrope',sans-serif; font-size: 13px; outline:none; }
        .ed-input:focus { border-color: rgba(255,255,255,0.5); }
      `}</style>
    </div>
  );
}

function ExtraEditor({ extra, saving, onSave }) {
  const [draft, setDraft] = useState(extra);
  useEffect(() => setDraft(extra), [extra]);
  const dirty = draft.name !== extra.name || draft.price_min !== extra.price_min || draft.price_max !== extra.price_max;
  return (
    <div className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-white/10 last:border-b-0 items-center">
      <input className="x-input col-span-5" data-testid={`extra-${extra.id}-name`} value={draft.name} onChange={(e)=>setDraft({...draft, name: e.target.value})} />
      <input className="x-input col-span-3 text-right" type="number" data-testid={`extra-${extra.id}-min`} value={draft.price_min} onChange={(e)=>setDraft({...draft, price_min: parseFloat(e.target.value||0)})} />
      <input className="x-input col-span-3 text-right" type="number" data-testid={`extra-${extra.id}-max`} value={draft.price_max} onChange={(e)=>setDraft({...draft, price_max: parseFloat(e.target.value||0)})} />
      <button data-testid={`extra-${extra.id}-save`} disabled={!dirty || saving} onClick={() => onSave(draft)} className="col-span-1 flex justify-end disabled:opacity-30" title="Save">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 hover:text-white text-zinc-400" />}
      </button>
      <style>{`
        .x-input { background:#050505; border:1px solid rgba(255,255,255,0.1); color:#fff; padding: 8px 10px; font-family:'Manrope',sans-serif; font-size: 13px; outline:none; }
        .x-input:focus { border-color: rgba(255,255,255,0.5); }
      `}</style>
    </div>
  );
}

export default Admin;
